// Tiny DOM builder: text is always inserted as text (no innerHTML), so user data cannot inject markup.

export type Child = Node | string | number | null | undefined | false | Child[];
type Attrs = Record<string, unknown> | null | undefined;

export function h<K extends keyof HTMLElementTagNameMap>(tag: K, attrs?: Attrs, ...children: Child[]): HTMLElementTagNameMap[K] {
  const el = document.createElement(tag);
  for (const [k, v] of Object.entries(attrs ?? {})) {
    if (v === undefined || v === null || v === false) continue;
    if (k.startsWith('on') && typeof v === 'function') el.addEventListener(k.slice(2).toLowerCase(), v as EventListener);
    else if (k === 'class') el.className = String(v);
    else if (k === 'value' || k === 'checked' || k === 'disabled' || k === 'hidden') (el as any)[k] = v;
    else el.setAttribute(k, v === true ? '' : String(v));
  }
  append(el, children);
  return el;
}

export function append(el: Node, children: Child[]): void {
  for (const c of children) {
    if (c === null || c === undefined || c === false) continue;
    if (Array.isArray(c)) append(el, c);
    else el.appendChild(c instanceof Node ? c : document.createTextNode(String(c)));
  }
}

export function replace(el: Element, ...children: Child[]): void {
  el.replaceChildren();
  append(el, children);
}

/** a modal <dialog>; resolves with the value passed to close(), or null when dismissed */
export function modal<T>(build: (close: (v: T | null) => void) => Child[], cls = ''): Promise<T | null> {
  return new Promise((resolve) => {
    const d = h('dialog', { class: 'modal ' + cls });
    let done = false;
    const close = (v: T | null): void => {
      if (done) return;
      done = true;
      d.close();
      d.remove();
      resolve(v);
    };
    d.addEventListener('cancel', (e) => {
      e.preventDefault();
      close(null);
    });
    d.addEventListener('click', (e) => e.target === d && close(null));
    append(d, build(close));
    document.body.append(d);
    d.showModal();
    d.querySelector<HTMLElement>('[autofocus]')?.focus();
  });
}

export function confirmDialog(title: string, text: string, ok = 'Да', danger = false): Promise<boolean> {
  return modal<boolean>((close) => [
    h('h2', null, title),
    h('p', null, text),
    h('div', { class: 'buttons' },
      h('button', { class: 'btn', onclick: () => close(false) }, 'Отмена'),
      h('button', { class: danger ? 'btn danger' : 'btn primary', autofocus: true, onclick: () => close(true) }, ok),
    ),
  ]).then((v) => v === true);
}

let toastBox: HTMLElement | null = null;
export function toast(text: string, kind: 'ok' | 'error' = 'ok'): void {
  if (!toastBox) document.body.append((toastBox = h('div', { class: 'toasts', role: 'status', 'aria-live': 'polite' })));
  const t = h('div', { class: 'toast ' + kind }, text);
  toastBox.append(t);
  setTimeout(() => t.classList.add('gone'), 3500);
  setTimeout(() => t.remove(), 4000);
}
